import { useEffect, useMemo, useState } from 'react'

import helixSearchV2Api from '../../api/helixSearchV2/HelixSearchV2Api'
import {
    HelixSearchLogStatusV2,
    HelixSearchSessionStatusV2,
} from '../../api/helixSearchV2/models/StartHelixSearchV2'
import AnimateNumberTickUp from '../common/animations/AnimateNumberTickUp'
import LoadingSpinner from '../common/loading/LoadingSpinner'
import Modal from '../common/modals/Modal'
import { HelixSearchV2TopPickBadge } from './HelixSearchV2TopPickBadge'

type Props = {
    sst: string
    logId: string
    onClose: () => void
    autoCloseDeadline?: number | null
    resultsLoaded?: boolean
}

enum HelixSearchStageLabel {
    Starting = 'Starting search...',
    Searching = 'Searching...',
    Processing = 'Processing results...',
    Selecting = 'Selecting Top Picks...',
    Preparing = 'Preparing results...',
    Complete = 'Search complete',
}

type LogStatusMetadata = {
    stage: HelixSearchStageLabel
    animateCounts?: boolean
    showProcessingProgress?: boolean
    isContxtScoreStage?: boolean
    contxtScoreComplete?: boolean
}

const LOG_STATUS_METADATA: Record<HelixSearchLogStatusV2, LogStatusMetadata> = {
    DISPATCH_PENDING: { stage: HelixSearchStageLabel.Starting },
    QUEUED: { stage: HelixSearchStageLabel.Starting },
    STARTED: { stage: HelixSearchStageLabel.Starting },
    SPLITTING_SEED_PERSONS: {
        stage: HelixSearchStageLabel.Searching,
        animateCounts: true,
    },
    SEARCHING_CANDIDATES: {
        stage: HelixSearchStageLabel.Searching,
        animateCounts: true,
    },
    SPLITTING_CANDIDATES: {
        stage: HelixSearchStageLabel.Processing,
    },
    COMBINING_RESULTS: {
        stage: HelixSearchStageLabel.Processing,
        showProcessingProgress: true,
    },
    GETTING_CONTXT_SCORE: {
        stage: HelixSearchStageLabel.Selecting,
        isContxtScoreStage: true,
    },
    COMPLETE: {
        stage: HelixSearchStageLabel.Preparing,
        contxtScoreComplete: true,
    },
    FAILED: {
        stage: HelixSearchStageLabel.Complete,
        contxtScoreComplete: true,
    },
    CANCELLING: {
        stage: HelixSearchStageLabel.Complete,
    },
    CANCELED: {
        stage: HelixSearchStageLabel.Complete,
        contxtScoreComplete: true,
    },
    EXPIRED: {
        stage: HelixSearchStageLabel.Complete,
        contxtScoreComplete: true,
    },
}

const isTerminalSessionStatus = (status: HelixSearchSessionStatusV2 | null) => {
    if (!status) {
        return false
    }
    return (
        status === 'COMPLETE' ||
        status === 'FAILED' ||
        status === 'CANCELED' ||
        status === 'EXPIRED'
    )
}

const POLL_DELAY_MS = 500
const SHOW_TOP_PICK_STATUSES: HelixSearchLogStatusV2[] = [
    'GETTING_CONTXT_SCORE',
    'COMPLETE',
    'FAILED',
    'CANCELLING',
    'CANCELED',
]

export default function HelixSearchV2ProgressModalCopy({
    sst,
    onClose,
    autoCloseDeadline = null,
    resultsLoaded = false,
}: Props): React.JSX.Element {
    const [sessionStatus, setSessionStatus] =
        useState<HelixSearchSessionStatusV2 | null>(null)
    const [logStatus, setLogStatus] = useState<HelixSearchLogStatusV2 | null>(
        null,
    )
    const [networkPersonCount, setNetworkPersonCount] = useState<number>(0)
    const [booleanSearchPersonCount, setBooleanSearchPersonCount] =
        useState<number>(0)
    const [hasLoadedCounts, setHasLoadedCounts] = useState(false)
    const [combinedResultCount, setCombinedResultCount] = useState<number>(0)
    const [llmAnalysisCountTarget, setLlmAnalysisCountTarget] =
        useState<number>(0)
    const [llmAnalysisCountActual, setLlmAnalysisCountActual] =
        useState<number>(0)
    const [topPickCount, setTopPickCount] = useState<number>(0)
    const [enabledLlmScoring, setEnabledLlmScoring] = useState<boolean>(false)

    const logStatusMetadata = logStatus
        ? LOG_STATUS_METADATA[logStatus]
        : undefined

    const showProcessingProgress = useMemo(() => {
        if (!logStatusMetadata?.showProcessingProgress) {
            return false
        }
        return booleanSearchPersonCount > 0
    }, [logStatusMetadata, booleanSearchPersonCount])

    const shouldAnimateCounts = useMemo(
        () => Boolean(logStatusMetadata?.animateCounts),
        [logStatusMetadata],
    )

    const processingProgressPercent = useMemo(() => {
        if (!showProcessingProgress) {
            return 0
        }
        const denominator = Math.max(1, booleanSearchPersonCount)
        const pct = Math.round((combinedResultCount / denominator) * 100)
        return Math.max(0, Math.min(100, pct))
    }, [showProcessingProgress, combinedResultCount, booleanSearchPersonCount])

    const showContxtScoreStage = useMemo(
        () => Boolean(logStatusMetadata?.isContxtScoreStage),
        [logStatusMetadata],
    )

    const contxtScoreProgressPercent = useMemo(() => {
        if (!showContxtScoreStage || !logStatus) {
            return 0
        }
        if (logStatusMetadata?.contxtScoreComplete) {
            return 100
        }
        const target = Math.max(0, llmAnalysisCountTarget)
        if (target === 0) {
            return 0
        }
        const pct = Math.round(
            (Math.max(0, llmAnalysisCountActual) / target) * 100,
        )
        return Math.max(0, Math.min(100, pct))
    }, [
        llmAnalysisCountActual,
        llmAnalysisCountTarget,
        logStatus,
        logStatusMetadata,
        showContxtScoreStage,
    ])

    // const contxtScoreStatusText = useMemo(() => {
    //     if (logStatusMetadata?.contxtScoreComplete) {
    //         return ''
    //     }
    //     if (llmAnalysisCountTarget <= 0) {
    //         return 'Selecting Top Picks...'
    //     }
    //     const safeTarget = Math.max(1, llmAnalysisCountTarget)
    //     const safeActual = Math.max(0, llmAnalysisCountActual)
    //     return `${safeActual} / ${safeTarget} candidates scored - ${topPickCount} top picks`
    // }, [
    //     llmAnalysisCountActual,
    //     llmAnalysisCountTarget,
    //     logStatusMetadata,
    //     topPickCount,
    // ])

    const hasSearchFailureStatus =
        sessionStatus === 'FAILED' || logStatus === 'FAILED'

    const hasTerminalSessionStatus = useMemo(
        () => isTerminalSessionStatus(sessionStatus),
        [sessionStatus],
    )

    const isSessionReadyToClose = useMemo(() => {
        if (!hasTerminalSessionStatus) {
            return false
        }
        if (sessionStatus === 'COMPLETE') {
            return resultsLoaded
        }
        return true
    }, [hasTerminalSessionStatus, resultsLoaded, sessionStatus])

    const showProcessingProgressSection =
        showProcessingProgress && !showContxtScoreStage
    const showContxtScoreStageSection = showContxtScoreStage
    const shouldAnimateCountsNow = shouldAnimateCounts
    const shouldShowTopPickCount =
        enabledLlmScoring &&
        (logStatus ? SHOW_TOP_PICK_STATUSES.includes(logStatus) : false)
    const summaryGridClassName = `grid grid-cols-1 gap-4 ${
        enabledLlmScoring ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
    }`
    const summaryCardClassName =
        'rounded-lg border border-gray-200 p-4 shadow-sm bg-white'

    const currentStageLabel = useMemo(() => {
        if (sessionStatus === 'COMPLETE' && !resultsLoaded) {
            return HelixSearchStageLabel.Preparing
        }
        if (sessionStatus && isTerminalSessionStatus(sessionStatus)) {
            return HelixSearchStageLabel.Complete
        }
        if (logStatusMetadata?.stage) {
            return logStatusMetadata.stage
        }
        return HelixSearchStageLabel.Starting
    }, [logStatusMetadata, resultsLoaded, sessionStatus])

    const showStageSpinner = !isSessionReadyToClose

    useEffect(() => {
        if (!hasSearchFailureStatus) {
            return
        }
        onClose()
    }, [hasSearchFailureStatus, onClose])

    useEffect(() => {
        setNetworkPersonCount(0)
        setBooleanSearchPersonCount(0)
        setHasLoadedCounts(false)
        setCombinedResultCount(0)
        setLlmAnalysisCountTarget(0)
        setLlmAnalysisCountActual(0)
        setTopPickCount(0)
        setEnabledLlmScoring(false)
    }, [sst])

    useEffect(() => {
        if (autoCloseDeadline == null) {
            return
        }

        const updateCountdown = () => {
            const msRemaining = autoCloseDeadline - Date.now()
            if (msRemaining <= 0) {
                return false
            }
            return true
        }

        updateCountdown()
        const intervalId = window.setInterval(() => {
            if (!updateCountdown()) {
                window.clearInterval(intervalId)
            }
        }, 200)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [autoCloseDeadline])

    useEffect(() => {
        let isMounted = true
        let timeoutHandle: number | undefined

        const poll = async () => {
            if (!isMounted) {
                return
            }

            try {
                const res = await helixSearchV2Api.getSearchStatus({
                    sst,
                })
                if (!isMounted) {
                    return
                }
                setSessionStatus(res.sessionStatus)
                setLogStatus(res.logStatus)
                setNetworkPersonCount(res.networkPersonCount ?? 0)
                setBooleanSearchPersonCount(res.booleanSearchPersonCount ?? 0)
                setCombinedResultCount(res.combinedResultCount ?? 0)
                setLlmAnalysisCountTarget(res.llmAnalysisCountTarget ?? 0)
                setLlmAnalysisCountActual(res.llmAnalysisCountActual ?? 0)
                setTopPickCount(res.topPickCount ?? 0)
                setHasLoadedCounts(true)
                setEnabledLlmScoring(res.enableLlmScoring ?? false)

                if (!isTerminalSessionStatus(res.sessionStatus)) {
                    timeoutHandle = window.setTimeout(poll, POLL_DELAY_MS)
                }
            } catch {
                if (!isMounted) {
                    return
                }
                timeoutHandle = window.setTimeout(poll, POLL_DELAY_MS)
            }
        }

        poll()

        return () => {
            isMounted = false
            if (timeoutHandle) {
                window.clearTimeout(timeoutHandle)
            }
        }
    }, [sst])

    return (
        <Modal
            title="Searching Network"
            onClose={onClose}
            size="large"
            allowUserToClose={false}
        >
            <div className="space-y-6">
                <section>
                    <div className="flex items-center justify-start gap-2">
                        <div className="flex-shrink-0">
                            <LoadingSpinner
                                show={showStageSpinner}
                                spinnerSize={28}
                                className="!w-auto !items-start !justify-start h-auto"
                            />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="text-base text-gray-900">
                                {currentStageLabel}
                            </p>
                        </div>
                    </div>
                </section>

                {hasLoadedCounts && (
                    <section className="space-y-2">
                        <div className={summaryGridClassName}>
                            <AnimateNumberTickUp
                                label="Network Searched"
                                value={networkPersonCount}
                                animate={shouldAnimateCountsNow}
                                className={summaryCardClassName}
                            />
                            <AnimateNumberTickUp
                                label="Candidates Found"
                                value={booleanSearchPersonCount}
                                animate={shouldAnimateCountsNow}
                                className={summaryCardClassName}
                            />
                            {enabledLlmScoring && shouldShowTopPickCount && (
                                <AnimateNumberTickUp
                                    label={
                                        <HelixSearchV2TopPickBadge
                                            pluralized={true}
                                        />
                                    }
                                    value={topPickCount}
                                    animate={shouldAnimateCountsNow}
                                    className={summaryCardClassName}
                                />
                            )}
                            {enabledLlmScoring && !shouldShowTopPickCount && (
                                <div
                                    className={`${summaryCardClassName} flex flex-col items-center justify-center gap-2 text-center text-gray-500`}
                                >
                                    <HelixSearchV2TopPickBadge
                                        pluralized={true}
                                    />
                                    <span>Pending...</span>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {showProcessingProgressSection && (
                    <section className="space-y-2">
                        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="mb-2 flex items-center justify-between text-xs text-ctxt-blue">
                                <span>Processing Results</span>
                                <span>{processingProgressPercent}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-ctxt-blue/10">
                                <div
                                    className="h-full rounded-full bg-ctxt-blue transition-[width] duration-300 ease-linear"
                                    style={{
                                        width: `${processingProgressPercent}%`,
                                    }}
                                />
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {combinedResultCount} /{' '}
                                {booleanSearchPersonCount} candidates processed
                            </p>
                        </div>
                    </section>
                )}

                {showContxtScoreStageSection && (
                    <section className="space-y-2">
                        <div className="rounded-lg border border-amber-200 bg-white p-4 shadow-sm">
                            <div className="mb-2 flex items-center justify-between text-xs text-amber-700">
                                <span>
                                    Finding{' '}
                                    <HelixSearchV2TopPickBadge
                                        pluralized={true}
                                    />
                                </span>
                                <span>{contxtScoreProgressPercent}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-amber-100">
                                <div
                                    className={`h-full rounded-full bg-amber-400 ${
                                        contxtScoreProgressPercent > 0 &&
                                        contxtScoreProgressPercent < 100
                                            ? 'animate-pulse'
                                            : ''
                                    }`}
                                    style={{
                                        width: `${contxtScoreProgressPercent}%`,
                                    }}
                                />
                            </div>
                            {/* <p className="mt-2 text-xs text-amber-700">
                                {contxtScoreStatusText}
                            </p> */}
                        </div>
                    </section>
                )}
            </div>
        </Modal>
    )
}
